import { graphql } from 'react-relay';
import debounce from 'lodash/debounce';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { EditorActions } from '@atlaskit/editor-core/actions';
import type { CollabEditPlugin } from '@atlaskit/editor-plugin-collab-edit/collabEditPluginType';
import type { CollabMetadataPayload, CollabDataPayload } from '@atlaskit/editor-common/collab';

import { isApolloError } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isStatusCodeError } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';
import { getStatusCode } from '@confluence/error-types/entry-points/isStatusCodeError.ts';
import { isNotFoundError } from '@confluence/error-types/entry-points/isNotFoundError.ts';
import {
	EDIT_PAGE_CLOSE_EXPERIENCE,
	COMPANY_HUB_EDITOR_CLOSE_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { getLogSafeErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { useDraftSyncAndPublishContext } from '@confluence/native-collab/entry-points/useDraftSyncAndPublishContext.ts';
import { BadStatusError } from '@confluence/network';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import { useGetCollabProviderState } from '../useGetCollabProviderState.ts';
import { useSkipDraftSync } from '../useSkipDraftSync/index.ts';

import type {
	useDraftSyncSetRelationMutation as useDraftSyncSetRelationMutation$data,
	useDraftSyncSetRelationMutationVariables as useDraftSyncSetRelationMutation$variables,
} from './__types__/useDraftSyncSetRelationMutation.ts';
import {
	RelationSourceType,
	RelationTargetType,
	RelationType,
} from './__types__/SetRelationMutation.ts';
import { useHasEditorContentChanged } from './useHasEditorContentChanged.ts';
import { useExperimentalDraftSyncMutation } from './useExperimentalDraftSyncMutation.ts';
const useDraftSyncSetRelationMutation = graphql`
	mutation useDraftSyncSetRelationMutation($input: UpdateRelationInput!) {
		updateRelation(input: $input) {
			targetKey
			sourceKey
			relationName
			url
		}
	}
`;
export type UseDraftSyncProps = {
	contentId: string;
	contentType: string;
	editorActions: EditorActions;
	isFirstParticipant: boolean;
	onCollabError: () => void;
	editorPresetAPI?: PublicPluginAPI<[CollabEditPlugin]> | null;
	editorSessionId?: string | null;
	viewMode?: boolean;
	onPublishUpdateTitleInPageTree?: () => void;
};

const DRAFT_SYNC_TIMEOUT = 5000;
export enum DRAFT_SYNC_ERROR {
	COLLAB_PROVIDER = 'DRAFT_SYNC_ERROR_COLLAB_PROVIDER',
	ACK_STATE = 'DRAFT_SYNC_ERROR_ACK_STATE',
	NO_ACCESS = 'DRAFT_SYNC_ERROR_NO_ACCESS',
}

// Error indicating we short-circuited draft sync call due to precondition not being met
export class DraftSyncAbortedError {
	constructor(public reason: DRAFT_SYNC_ERROR) {
		this.reason = reason;
	}
}

// Error indicating we received an error response from the server, for errors we want to handle elsewhere in the app (e.g. displaying not permitted/not found screens in EditorWrapper)
export class DraftSyncResponseError extends Error {
	constructor(
		public message: string,
		public statusCode?: number,
	) {
		super(message);
		this.statusCode = statusCode;
	}
}

const DRAFT_SYNC_EXCLUDED_STEPS = ['override-document', 'atlaskit-analytics'];

/**
 * Hook used to trigger draft sync for Edit page.
 * Draft sync is a step of the saving flow that writes the current state of the document from the collab provider to Confluence Monolith after a user makes changes.
 *
 * **WARNING**: This hook is only inteded for Edit page, and its functionality should be skipped for Live docs.
 * Please ensure that any functionality added to this hook does not affect Live docs.
 *
 * @see useEditorDraftSyncAction for Live docs draft sync
 */
export const useDraftSync = ({
	contentId,
	contentType,
	editorActions,
	isFirstParticipant,
	onCollabError,
	editorPresetAPI,
	editorSessionId,
	viewMode,
}: UseDraftSyncProps): {
	// This is needed for the following occasions:
	// when user clicks on a template from the templates panel (beforeTemplateSelect)
	cancelDebouncedSaveDraft: () => void;
	triggerSaveDraft: () => void;
	saveDraftError: DraftSyncResponseError | null;
	saveDraft: ({ isOnClose }?: any) => Promise<void>;
	saveDraftPromise: Promise<void> | null;
	isSavingDraft: boolean;
} => {
	const [spaceKey] = usePageSpaceKey();
	const spaceId = useSpaceId();
	const draftSyncDebounceMaxWait = StatsigConfigurations.getNumber(
		'draft_sync_debounce_max_wait',
		0,
	);
	const { hasEditorContentChangedByAnyUser, hasEditorContentChangedByLocalUserExcludingComments } =
		useHasEditorContentChanged({
			editorPresetAPI,
		});
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [draftSyncError, setDraftSyncError] = useState<DraftSyncResponseError | null>(null);
	const saveDraftPromiseRef = useRef<Promise<void> | null>(null);
	const didUnmount = useRef(false);
	const [experimentalDraftSyncMutation] = useExperimentalDraftSyncMutation();
	const [setRelationMutation] = useMutationInterop<
		useDraftSyncSetRelationMutation$data,
		useDraftSyncSetRelationMutation$variables
	>(useDraftSyncSetRelationMutation);
	const [{ collabEditProvider, collabEditInitialized }, { setIsDraftSyncPending }] =
		useNativeCollabState();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { getIsPublishing } = useDraftSyncAndPublishContext();
	const isLivePage = useIsCurrentPageLive(); // Live pages use draft sync v2 via useEditorDraftSyncAction instead of this hook
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const getCollabProviderState = useGetCollabProviderState({
		editorActions,
		collabEditProvider,
		forceAwaitCommitLocalSteps: true, // using getResolvedEditorState ensures any active media uploads are completed, and that we sync the "confirmed" version from NCS backend
		reason: 'draft-sync',
	});
	const { isEditorDirty } = useSkipDraftSync({ contentId });

	const setRelation = useCallback(
		async (relationType: RelationType) => {
			try {
				await setRelationMutation({
					variables: {
						input: {
							relationName: relationType,
							sourceKey: 'current',
							sourceType: RelationSourceType.user,
							targetType: RelationTargetType.content,
							targetKey: contentId,
							targetStatus: 'draft',
						},
					},
				});
			} catch (e) {
				getMonitoringClient().submitError(e, {
					attribution: Attribution.PAGE_EXTENSIONS,
				});
				markErrorAsHandled(e);
			}
		},
		[contentId, setRelationMutation],
	);

	useEffect(() => {
		return () => {
			didUnmount.current = true;
		};
	}, []);

	const hasTriggeredContentChange = useRef<boolean>(false);
	useEffect(() => {
		if (
			!hasEditorContentChangedByLocalUserExcludingComments ||
			!window ||
			hasTriggeredContentChange.current
		) {
			return;
		}

		const later = window.requestIdleCallback || window.requestAnimationFrame;
		hasTriggeredContentChange.current = true;

		later(async () => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'nativeCollabComponent',
					action: 'edited',
					actionSubject: 'editor',
					containerType: 'space',
					containerId: spaceId,
					objectType: 'page',
					objectId: contentId,
					attributes: { isLivePage, editorSessionId },
				},
			}).fire();

			// For Draft Sync V2, relations are set on the backend
			if (isLivePage) {
				return;
			} else {
				void setRelation(RelationType.touched);
			}
		});
	}, [
		contentId,
		isLivePage,
		editorSessionId,
		createAnalyticsEvent,
		hasEditorContentChangedByLocalUserExcludingComments,
		viewMode,
		setRelation,
		spaceId,
	]);

	const saveDraft = useCallback(
		(awaitCommitLocalSteps = false): Promise<void> => {
			/**
			 * The draft sync should be skipped if:
			 * - The current user is not the first participant
			 * - The page is currently being published
			 * - Collab edit is not initialized
			 * - The hook has unmounted (the user has navigated away from the editor)
			 */
			if (
				!isFirstParticipant ||
				getIsPublishing() ||
				!collabEditInitialized ||
				didUnmount.current ||
				isLivePage
			) {
				setIsDraftSyncPending(false);
				return Promise.resolve();
			}
			if (saveDraftPromiseRef.current != null) {
				return saveDraftPromiseRef.current;
			}
			setIsSaving(true);
			saveDraftPromiseRef.current = new Promise<void | undefined>(async (resolve, reject) => {
				if (!collabEditProvider) {
					return reject(new DraftSyncAbortedError(DRAFT_SYNC_ERROR.COLLAB_PROVIDER));
				}

				if (draftSyncError) {
					// We don't want to continue saving the draft in the background
					return reject(new DraftSyncAbortedError(DRAFT_SYNC_ERROR.NO_ACCESS));
				}

				let ackState;

				try {
					ackState = await getCollabProviderState(awaitCommitLocalSteps);

					if (!ackState) {
						return reject(new DraftSyncAbortedError(DRAFT_SYNC_ERROR.ACK_STATE));
					}
				} catch (err) {
					onCollabError();
					reject(err);
				}

				const { title, content, stepVersion } = ackState;

				if (!isEditorDirty(stepVersion, title)) {
					return resolve();
				}

				try {
					await experimentalDraftSyncMutation({
						variables: {
							contentId,
							contentType,
							content: JSON.stringify(content),
							ncsStepVersion: stepVersion.toString(),
							title: title as string,
						},
					});

					resolve();
				} catch (e) {
					// If there's a 403 or 404 from saving the draft it's most likely because the permissions have changed, so we should disable the editor and show a flag (CONFCLOUD-66357)
					if (isUnauthorizedError(e)) {
						setDraftSyncError(new DraftSyncResponseError(e.message, 403));
					} else if (isNotFoundError(e)) {
						setDraftSyncError(new DraftSyncResponseError(e.message, 404));
					} else if (
						isStatusCodeError(e, '503') &&
						e?.message?.includes('Site temporarily unavailable')
					) {
						setDraftSyncError(new DraftSyncResponseError(e.message, 503));
					} else if (isStatusCodeError(e, '413')) {
						// If there's a 413 from saving the draft, the page content is too large. Show a flag
						// to inform the user.
						setDraftSyncError(new DraftSyncResponseError(e.message, 413));
					}
					reject(e);
				}
			}).finally(() => {
				setIsDraftSyncPending(false);
				setIsSaving(false);
				saveDraftPromiseRef.current = null;
			});
			return saveDraftPromiseRef.current;
		},
		[
			isFirstParticipant,
			getCollabProviderState,
			collabEditProvider,
			collabEditInitialized,
			contentId,
			contentType,
			experimentalDraftSyncMutation,
			onCollabError,
			getIsPublishing,
			isEditorDirty,
			draftSyncError,
			setIsDraftSyncPending,
			isLivePage,
		],
	);

	const saveDraftWithExperienceTracking = useCallback(
		async ({ isOnClose }: any = {}): Promise<void> => {
			if (isLivePage) {
				return;
			}

			// suppressing only `experimentalDraftSyncMutation` to match former behaviour, but it may be desirable to prevent bubbling up any draft sync errors
			if (!isOnClose)
				return saveDraft(false).catch((error) =>
					error instanceof DraftSyncAbortedError ? Promise.reject(error) : Promise.resolve(),
				);

			const name = isCompanyHubSpaceKey(spaceKey)
				? COMPANY_HUB_EDITOR_CLOSE_EXPERIENCE
				: EDIT_PAGE_CLOSE_EXPERIENCE;
			experienceTracker.start({
				name,
				timeout: ExperienceTimeout.EDITOR_PUBLISH,
				attributes: {
					contentId,
					collabService: 'ncs',
					isCompanyHub: isCompanyHubSpaceKey(spaceKey),
				},
			});

			// Track whether the content is empty on editor close. This can't be done
			// in saveDraft by storing the content length result from ackState because
			// the function short circuits for non-primary participants.
			let isContentEmpty: boolean | undefined = undefined;
			try {
				const ackState = await getCollabProviderState(false);
				const contentLength = ackState?.content?.content?.length ?? undefined;
				isContentEmpty = contentLength === undefined ? undefined : contentLength === 0;
			} catch {
				// intentionally empty - we don't need to handle this error
			}

			// on editor close, we want to sync any unconfirmed steps before saving draft
			return saveDraft(true).then(
				(value) => {
					experienceTracker.succeed({
						name,
						attributes: {
							contentId,
							collabService: 'ncs',
							...(isContentEmpty !== undefined ? { isContentEmpty } : {}),
						},
					});
					return value;
				},
				(error) => {
					let traceId: string | null | undefined = null;
					if (!(error instanceof DraftSyncAbortedError)) {
						const errorObj = isApolloError(error) ? error?.graphQLErrors[0] : error;

						const statusCode = getStatusCode(errorObj) || getStatusCode(errorObj?.originalError);
						const numStatus = statusCode && parseInt(statusCode, 10);

						if (
							BadStatusError.isBadStatusError(errorObj) ||
							BadStatusError.isBadStatusError(errorObj?.originalError)
						) {
							traceId =
								getLogSafeErrorAttributes(errorObj).traceId || errorObj?.originalError?.traceId;
						}

						if (numStatus && !isNaN(numStatus) && numStatus >= 400 && numStatus < 500) {
							experienceTracker.abort({
								name,
								reason: `User error with status code ${statusCode}`,
								attributes: {
									contentId,
									collabService: 'ncs',
									traceId: traceId ?? 'unknown',
									...(isContentEmpty !== undefined ? { isContentEmpty } : {}),
								},
							});
							return;
						}
					}

					experienceTracker.stopOnError({
						name,
						error,
						attributes: {
							contentId,
							collabService: 'ncs',
							traceId: traceId ?? 'unknown',
							...(isContentEmpty !== undefined ? { isContentEmpty } : {}),
						},
					});
				},
			);
		},
		[saveDraft, experienceTracker, contentId, spaceKey, getCollabProviderState, isLivePage],
	);

	const debouncedSaveDraft = useMemo(
		() =>
			debounce(saveDraftWithExperienceTracking, DRAFT_SYNC_TIMEOUT, {
				...(draftSyncDebounceMaxWait > 0 && { maxWait: draftSyncDebounceMaxWait }),
			}),
		[saveDraftWithExperienceTracking, draftSyncDebounceMaxWait],
	);

	const cancelDebouncedSaveDraft = useCallback(
		(): void => debouncedSaveDraft.cancel(),
		[debouncedSaveDraft],
	);

	const triggerSaveDraft = useCallback((): void => {
		if (isLivePage) {
			return;
		}
		setIsDraftSyncPending(true);
		void debouncedSaveDraft();
	}, [debouncedSaveDraft, setIsDraftSyncPending, isLivePage]);

	const handleOnMetadataChanged = useCallback(
		(input: CollabMetadataPayload) => {
			// do not trigger a draft sync if it is just metadata change from the editor
			if (input?.metadataSource === 'client') {
				triggerSaveDraft();
			}
		},
		[triggerSaveDraft],
	);

	const handleDocumentUpdate = useCallback(
		(input: CollabDataPayload) => {
			// do not trigger a draft sync:
			// 		- if the step is an override-document step which comes from backend RESET call (so monolith is source of truth and already in sync)
			// 		- for auto-generated analytics steps
			const isExcludedSteps = input?.json?.every(
				(step) => !!step.stepType && DRAFT_SYNC_EXCLUDED_STEPS.includes(step.stepType),
			);
			if (hasEditorContentChangedByAnyUser && !isExcludedSteps && !isLivePage) {
				triggerSaveDraft();
			}
		},
		[hasEditorContentChangedByAnyUser, triggerSaveDraft, isLivePage],
	);

	const flushSaveDraft = useCallback(() => {
		void debouncedSaveDraft.flush();
	}, [debouncedSaveDraft]);

	useEffect(() => {
		if (!collabEditProvider || !collabEditInitialized || isLivePage) {
			return;
		}
		window.addEventListener('beforeunload', flushSaveDraft);
		return () => {
			window.removeEventListener('beforeunload', flushSaveDraft);
		};
	}, [collabEditInitialized, collabEditProvider, flushSaveDraft, isLivePage]);

	useEffect(() => {
		if (!isFirstParticipant || !collabEditProvider || isLivePage) {
			return;
		}
		collabEditProvider.on('data', handleDocumentUpdate);
		collabEditProvider.on('metadata:changed', handleOnMetadataChanged);
		return () => {
			collabEditProvider.off('data', handleDocumentUpdate);
			collabEditProvider.off('metadata:changed', handleOnMetadataChanged);
		};
	}, [
		collabEditProvider,
		triggerSaveDraft,
		handleDocumentUpdate,
		isFirstParticipant,
		isLivePage,
		handleOnMetadataChanged,
	]);

	return {
		// This is needed for the following occasions:
		// when user clicks on a template from the templates panel (beforeTemplateSelect)
		cancelDebouncedSaveDraft,
		triggerSaveDraft,
		saveDraftError: draftSyncError,
		saveDraft: saveDraftWithExperienceTracking,
		saveDraftPromise: saveDraftPromiseRef.current,
		isSavingDraft: isSaving,
	};
};
